'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { TransitionStatus } from 'react-transition-group';
import { useTranslations } from 'next-intl';
import { useStore, useEvent, useUnit } from 'effector-react';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import { SearchResultList } from '@nx-frontend/features/notes-list';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useHotKey } from '@nx-frontend/shared/hooks';
import { $currentWorkspace } from '@nx-frontend/shared/store/current-workspace';
import { $menuState } from '@nx-frontend/shared/store/feature-menu-tree';
import { $members } from '@nx-frontend/shared/store/members';
import {
  $searchValue,
  searchValueChanged,
  $searchResult,
  fetchSearchEvent,
  resetSearchEvent,
  FetchSearchParams,
} from '@nx-frontend/shared/store/search';
import { clearBreadcrumbsSpecialPagesVisitedEvent } from '@nx-frontend/shared/store/webnotes-breadcrumbs';
import { MenuItemType, NoteDto, MenuType, MenuItem } from '@nx-frontend/shared/types';
import { SearchNoteData, NoteItemType } from '@nx-frontend/shared/types';
import { MenusState } from '@nx-frontend/shared/types';

interface SearchModalProps {
  closePopUp: () => void;
  transitionStatus?: TransitionStatus;
  isFullScreen?: boolean;
}

const MIN_SEARCH_CHARS = 3;

const highlightText = (title: string, matches: string[]): string => {
  if (!matches || 0 === matches.length) {
    return title;
  }
  let highlightedTitle = title;
  matches.sort((a, b) => b.length - a.length);
  matches.forEach((match) => {
    const escapedMatch = match.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(escapedMatch, 'gi');
    highlightedTitle = highlightedTitle.replace(regex, (matched) => `<b>${matched}</b>`);
  });
  return highlightedTitle;
};
const findElement = (globalId: string, items: NoteDto[]): NoteDto | undefined => {
  return items.find((item) => item.globalId === globalId);
};
const isEmoji = (char: string): boolean => {
  const emojiPattern = /\p{Emoji}/u;
  return emojiPattern.test(char);
};
const truncateName = (name: string): string => {
  const maxLength = 15;
  let currentLength = 0;
  const characters = [...name];
  for (let i = 0; i < characters.length; i++) {
    const char = characters[i];
    currentLength += isEmoji(char) ? 2 : 1;
    if (currentLength >= maxLength) {
      return `${characters.slice(0, i).join('')}...`;
    }
  }
  return name;
};
const findBreadcrumbs = (globalId: string, items: NoteDto[]): [string[], string] => {
  const element = findElement(globalId, items);
  if (!element) {
    return [['', ''], ''];
  }

  const generateBreadcrumbs = (itemId: string): string[] => {
    const currentItem = findElement(itemId, items);
    if (!currentItem || currentItem.parentId === 'root') {
      return [truncateName(currentItem?.title || '')];
    } else {
      return [truncateName(currentItem.title), ...generateBreadcrumbs(currentItem.parentId)];
    }
  };

  const breadcrumbs = generateBreadcrumbs(globalId);

  if (breadcrumbs.length <= 2) {
    return [breadcrumbs, ''];
  }

  const firstAndLast = [breadcrumbs[0], breadcrumbs[breadcrumbs.length - 1]];
  const middle = breadcrumbs.slice(1, -1).join(' • ');

  return [firstAndLast, middle];
};
const getBreadcrumbs = (
  globalId: string,
  parentId: string,
  items: NoteDto[],
  menuState: MenusState,
  isPortalShare: boolean
): string => {
  if (isPortalShare) {
    let currentPage: MenuItem | null = null;
    let currentPageMenuType: MenuType | null = null;
    for (const menu of [MenuType.PORTAL_SIDEBAR, MenuType.PORTAL_FOOTER, MenuType.PORTAL_TOPBAR]) {
      for (const id in menuState[menu].entities) {
        const item = menuState[menu].entities[id];
        if (item.targetId === globalId) {
          currentPage = item;
          break;
        }
      }
      if (null !== currentPage) {
        currentPageMenuType = menu;
        break;
      }
    }
    if (currentPage && currentPageMenuType) {
      if (!currentPage.parentId) {
        let position = '';
        switch (currentPage.positionType) {
          case 'top':
            position = 'Topbar';
            break;
          case 'sidebar':
            position = 'Sidebar';
            break;
          case 'footer':
            position = 'Footer';
            break;
        }
        return position || '';
      } else {
        const positionType = currentPage.positionType || '';
        let position = '';
        switch (positionType) {
          case 'top':
            position = 'Topbar';
            break;
          case 'sidebar':
            position = 'Sidebar';
            break;
          case 'footer':
            position = 'Footer';
            break;
        }
        const parentPage = menuState[currentPageMenuType].entities[currentPage.parentId];
        if (parentPage.parentId) {
          return position ? `${position} • ... • ${parentPage.name}` : parentPage.name;
        } else {
          return position ? `${position} • ${parentPage.name}` : parentPage.name;
        }
      }
    } else {
      return '';
    }
  }
  const [firstAndLast, middle] = findBreadcrumbs(parentId, items);
  if (0 < middle.length) {
    return `${firstAndLast[1]} • ... • ${firstAndLast[0]}`;
  } else {
    if (1 < firstAndLast.length) {
      return `${firstAndLast[1]} • ${firstAndLast[0]}`;
    }
  }
  return firstAndLast[0];
};

export const SearchModalWrapper = ({ closePopUp, transitionStatus, isFullScreen }: SearchModalProps) => {
  const abortControllerRef = useRef<AbortController | null>(null);
  const [items, setItems] = useState<SearchNoteData[]>([]);
  const [loading, setLoading] = useState(true);
  const [mouseDownInsidePopup, setMouseDownInsidePopup] = useState(false);
  const isLatestQuery = useRef(true);
  useHotKey('Escape', function onEnter() {
    closePopUp();
  });
  const searchValue = useStore($searchValue);
  const setSearchValue = useEvent(searchValueChanged);
  const resetSearch = useEvent(resetSearchEvent);
  const search = useEvent(fetchSearchEvent);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [currentWorkspace, searchResult, members, menuState] = useUnit([
    $currentWorkspace,
    $searchResult,
    $members,
    $menuState,
  ]);
  const [clearBreadcrumbsSpecialPagesVisited] = useEvent([clearBreadcrumbsSpecialPagesVisitedEvent]);
  const t = useTranslations('webnotes');
  const showAuthor = members && members.count > 1;
  const divRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('692px');
  const debouncedFunctionRef = useRef(
    debounce(
      (searchParams: FetchSearchParams) => {
        resetSearch();
        search(searchParams);
      },
      500,
      { leading: false, trailing: true }
    )
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (MIN_SEARCH_CHARS <= value.length) {
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        abortControllerRef.current = new AbortController();
        resetSearch();
        debouncedFunctionRef.current({
          workspaceId: currentWorkspace.globalId,
          getMatches: true,
          buildExcerpts: true,
          counters: true,
          query: value,
          signal: abortControllerRef.current.signal,
        });
      }
    },
    [currentWorkspace.globalId, setSearchValue, resetSearch]
  );
  const handleKeyPress = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Enter') {
        if (searchValue.length >= MIN_SEARCH_CHARS) {
          if (abortControllerRef.current) {
            abortControllerRef.current.abort();
          }
          abortControllerRef.current = new AbortController();
          resetSearch();
          search({
            workspaceId: currentWorkspace.globalId,
            getMatches: true,
            buildExcerpts: true,
            counters: true,
            query: searchValue,
            signal: abortControllerRef.current.signal,
          });
        }
      }
    },
    [currentWorkspace.globalId, resetSearch, search, searchValue]
  );
  const handleContentClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    clearBreadcrumbsSpecialPagesVisited();
  };
  const clearInput = () => {
    setSearchValue('');
  };
  const focusInput = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };
  useEffect(() => {
    focusInput();
  }, []);
  useEffect(() => {
    function updateMaxHeight() {
      if (divRef.current) {
        const divPosition = (divRef.current as HTMLElement).getBoundingClientRect().top || 0;
        const windowHeight = (window.innerHeight || document.documentElement.clientHeight) - 16;
        const calculatedMaxHeight = Math.min(windowHeight - divPosition, 692);
        setMaxHeight(`${calculatedMaxHeight}px`);
      }
    }
    if (!searchResult.initial || 0 < items.length) {
      updateMaxHeight();
      window.addEventListener('resize', updateMaxHeight);
      return () => window.removeEventListener('resize', updateMaxHeight);
    }
  }, [searchResult, items]);
  const classNamesTransition: Record<string, boolean> = {};
  if (!transitionStatus) {
    transitionStatus = 'entered';
  }
  if (transitionStatus === 'entering' || transitionStatus === 'entered') {
    classNamesTransition['scale-100'] = true;
  } else if (transitionStatus === 'exiting' || transitionStatus === 'exited') {
    classNamesTransition['scale-90'] = true;
  }

  const classNamesBackground = isFullScreen ? 'w-[calc(100%-50px)] min-h-[calc(100%-20px)] overflow-auto' : '';

  useEffect(() => {
    if (searchResult.initial && searchResult.notes.length === 0 && !searchResult.isLoaded) {
      debouncedFunctionRef.current({
        workspaceId: currentWorkspace.globalId,
        getMatches: true,
        buildExcerpts: true,
        counters: true,
        query: '',
      });
    }
  }, [currentWorkspace.globalId, searchResult.initial, searchResult.isLoaded, searchResult.notes.length]);

  useEffect(() => {
    if (isLatestQuery.current) {
      const newItems: SearchNoteData[] = [];
      for (const item of searchResult.entities[currentWorkspace.globalId] || []) {
        const note = searchResult.notes.find((note) => note.globalId === item.noteGlobalId);
        if (note) {
          newItems.push({
            id: `${NoteItemType.NOTE}#${note.globalId}`,
            type: MenuItemType.NOTE,
            globalId: note.globalId,
            workspaceId: note.workspaceId,
            name: highlightText(note.title, item.matches),
            icon: note.emoji || 'page',
            userId: note.userId,
            updatedAt: note.updatedAt,
            createdAt: note.createdAt,
            parentId: `${NoteItemType.NOTES_FOLDER}#${note.parentId}`,
            index: 0,
            visible: true,
            url: '',
            slug: '',
            openInNewTab: false,
            hasChildren: false,
            shared: note.shared,
            isPortalShared: note.isSharedForPortal || false,
            breadcrumbs: getBreadcrumbs(
              note.globalId,
              note.parentId,
              searchResult.folders,
              menuState,
              note.isSharedForPortal || false
            ),
          });
        }
      }
      setItems(newItems);
      setLoading(false);
    }
  }, [searchResult, currentWorkspace, menuState]);

  const handleModalMouseDown = () => {
    setMouseDownInsidePopup(false);
  };
  const handlePopupMouseDown = (e: React.MouseEvent) => {
    e.stopPropagation();
    setMouseDownInsidePopup(true);
  };
  const handleModalMouseUp = () => {
    if (!mouseDownInsidePopup) {
      closePopUp();
    }
  };

  return (
    <div
      data-cy="search-popup"
      id="ModalContainer"
      className="fixed inset-0 flex justify-center z-40"
      style={{ backdropFilter: 'blur(4px)' }}
      onMouseDown={handleModalMouseDown}
      onMouseUp={handleModalMouseUp}
    >
      <div data-cy="close-search-popup" onClick={closePopUp} className="absolute top-4 right-4 cursor-pointer">
        <Icon icon={IconsMap.CLOSE} className="text-neutral-500 w-[24px] h-[24px]" />
      </div>
      <div
        className={classNames(
          `p-5 z-50 transition-transform duration-200 ease-out will-change-transform`,
          classNamesBackground,
          {
            absolute: true,
            ...classNamesTransition,
          }
        )}
        onMouseDown={handlePopupMouseDown}
        onClick={handleContentClick}
        style={{ marginTop: '2%' }}
      >
        <div className="inline-flex flex-col items-start space-y-4">
          <div className="inline-flex flex-row items-center">
            <div
              className="w-[797px] h-[48px] bg-white cursor-pointer border-[1px] border-solid border-neutral-200 rounded-[16px] flex items-center"
              style={{
                border: '1px solid var(--light-theme-border-main, #E5E5E5)',
                background: 'var(--light-theme-surfaces-main, #FFF)',
                boxShadow: '0px 2px 15px 0px rgba(163, 168, 173, 0.22)',
              }}
            >
              <div className="flex items-center w-full h-full mx-[16px] my-[12px] gap-2 rounded-[16px]">
                <Icon icon={IconsMap.SEARCH} className="text-neutral-500 w-[24px] h-[24px]" />
                <input
                  data-cy="search-popup-input"
                  ref={inputRef}
                  className="h-full w-full outline-none text-[14px] text-neutral-800 placeholder:text-neutral-500 pr-2"
                  type="text"
                  id="search"
                  placeholder={t('webnotes-search_pages_by_name')}
                  value={searchValue}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  style={{ caretColor: 'rgb(6,182,212)' }}
                />
                {searchValue.length ? (
                  <div className="relative group inline-block">
                    <div data-cy="clear-search-input" onClick={clearInput} className="cursor-pointer">
                      <Icon icon={IconsMap.CLOSE} className="text-neutral-500 w-4 h-4" />
                    </div>
                    <div
                      data-cy="clear-search-tooltip"
                      className="text-[10px] absolute opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300 -top-6 left-1/2 transform -translate-x-1/2 p-1 bg-neutral-800 text-white rounded-[2px]"
                    >
                      Clear
                    </div>
                  </div>
                ) : (
                  <div>
                    <Icon icon={IconsMap.ARROW_FORWARD} className="text-neutral-500 w-[16px] h-[16px]" />
                  </div>
                )}
              </div>
            </div>
            {/* <div className='flex w-9 h-9 p-3 mx-[16px] justify-center items-center space-x-2.5 flex-shrink-0 rounded-full border '>
              <Icon icon={IconsMap.FILTER} className="text-neutral-500" />
            </div> */}
          </div>
          {(!searchResult.initial || 0 < items.length) && searchValue === searchResult.query ? (
            <div data-cy="search-results" className="inline-flex flex-row items-center">
              <div
                className="w-[797px] bg-white border-[1px] border-solid border-neutral-200 rounded-[16px] flex"
                style={{
                  border: '1px solid var(--light-theme-border-main, #E5E5E5)',
                  background: 'var(--light-theme-surfaces-main, #FFF)',
                  boxShadow: '0px 2px 15px 0px rgba(163, 168, 173, 0.22)',
                }}
              >
                <div className="w-full h-full flex flex-col">
                  {searchResult.initial ? (
                    <div className="text-[10px] font-normal uppercase py-2 px-4 gap-1">
                      {t('webnotes-recently_updated_pages')}
                    </div>
                  ) : (
                    <div className="py-1 gap-1"> </div>
                  )}
                  {searchResult.initial || searchResult.isLoaded ? (
                    items.length === 0 &&
                    MIN_SEARCH_CHARS <= searchValue.length &&
                    !loading &&
                    searchValue === searchResult.query ? (
                      <div data-cy="empty-search-results" className="text-[14px] font-normal mb-2 py-2 px-4 gap-1">
                        <span role="img" aria-label="Pensive Face">
                          😔
                        </span>{' '}
                        {t('webnotes-empty_search_result', { searchValue })}
                      </div>
                    ) : (
                      <div ref={divRef} className="w-full h-full overflow-y-auto" style={{ maxHeight }}>
                        <table data-cy="search-results-table" className="table w-full">
                          <thead
                            data-cy="search-results-header"
                            className="w-full table-header-group sticky top-0 z-10 bg-white"
                          >
                            <tr className="table-row">
                              <th className="table-cell text-left">
                                <div className="py-2.5 px-4 flex items-center gap-1">
                                  <span className="text-[10px] font-normal uppercase">{t('webnotes-name')}</span>
                                </div>
                                <div className="w-full absolute inset-x-0 bottom-0 h-[1px] bg-neutral-200"></div>
                              </th>
                              {showAuthor ? (
                                <th className="table-cell text-left">
                                  <div className="flex items-center p-2">
                                    <span className="text-[10px] font-normal uppercase">{t('webnotes-author')}</span>
                                  </div>
                                </th>
                              ) : null}
                              <th className="table-cell text-left">
                                <div className="p-2 flex items-center gap-1">
                                  <span className="text-[10px] font-normal uppercase">{t('webnotes-updated')}</span>
                                </div>
                              </th>
                              <th className="table-cell text-left">
                                <div className="p-2 flex items-center gap-1">
                                  <span className="text-[10px] font-normal uppercase">{t('webnotes-created')}</span>
                                </div>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="table-row-group">
                            <SearchResultList items={items} showAuthor={showAuthor} closePopUp={closePopUp} />
                          </tbody>
                        </table>
                      </div>
                    )
                  ) : null}
                  {searchResult?.isFetching && (
                    <div className="w-full p-4 inline-flex justify-center">
                      <Spinner />{' '}
                    </div>
                  )}
                </div>
              </div>
            </div>
          ) : (
            searchResult?.isFetching && (
              <div data-cy="search-results" className="inline-flex flex-row items-center">
                <div
                  className="w-[797px] bg-white border-[1px] border-solid border-neutral-200 rounded-[16px] flex"
                  style={{
                    border: '1px solid var(--light-theme-border-main, #E5E5E5)',
                    background: 'var(--light-theme-surfaces-main, #FFF)',
                    boxShadow: '0px 2px 15px 0px rgba(163, 168, 173, 0.22)',
                  }}
                >
                  <div className="w-full h-full flex flex-col">
                    <div className="w-full p-4 inline-flex justify-center">
                      <Spinner />{' '}
                    </div>
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};
